import type { CurriculumPlan } from '../../types';

// Page-level mock data for Curriculum Plans page
// Export from lib/mockData for actual data

export {
  mockCurriculumPlans,
  getCurriculumPlansByYearGroup,
  getCurriculumPlansBySubject,
  getCorePlans,
  getElectivePlans,
  getTotalLessonsPerWeek,
} from '../../lib/mockData/curriculum';

// Additional page-specific helpers for UI state management

export interface CurriculumPlanTableRow extends CurriculumPlan {
  subjectName?: string;
  subjectCode?: string;
  yearGroupName?: string;
  teacherCount?: number;
  roomRequirement?: string;
}

// Teaching models for filtering
export const teachingModels = [
  { value: 'whole-class', label: 'Whole Class' },
  { value: 'sets', label: 'Sets' },
  { value: 'bands', label: 'Bands' },
  { value: 'options', label: 'Options' },
] as const;

// Year groups for filtering
export const yearGroups = [
  { value: 'year-7', label: 'Year 7' },
  { value: 'year-8', label: 'Year 8' },
  { value: 'year-9', label: 'Year 9' },
  { value: 'year-10', label: 'Year 10' },
  { value: 'year-11', label: 'Year 11' },
  { value: 'year-12', label: 'Year 12' },
  { value: 'year-13', label: 'Year 13' },
] as const;

// Constraint presets for common patterns
export const constraintPresets = {
  standard: {
    requiresDoubleLessons: false,
    requiresSpecialistRoom: false,
    isElective: false,
  },
  science: {
    requiresDoubleLessons: true,
    doubleCount: 2,
    requiresSpecialistRoom: true,
    requiredRoomType: 'laboratory' as const,
    isElective: false,
  },
  practical: {
    requiresDoubleLessons: true,
    doubleCount: 1,
    requiresSpecialistRoom: true,
    isElective: true,
  },
  option: {
    requiresDoubleLessons: false,
    requiresSpecialistRoom: false,
    isElective: true,
  },
} as const;

// Helper to calculate total teaching hours per subject
export const calculateSubjectTeachingHours = (subjectId: string, plans: CurriculumPlan[]): number => {
  return plans.filter((p) => p.subjectId === subjectId).reduce((total, plan) => total + plan.lessonsPerWeek, 0);
};

// Helper to validate plan constraints
export const validatePlanConstraints = (plan: CurriculumPlan): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (plan.lessonsPerWeek < 1) {
    errors.push('Lessons per week must be at least 1');
  }

  if (plan.constraints.requiresDoubleLessons && !plan.constraints.doubleCount) {
    errors.push('Double lessons require a double count');
  }

  if (plan.constraints.requiresSpecialistRoom && !plan.constraints.requiredRoomType) {
    errors.push('Specialist room requires a room type');
  }

  if (plan.teachingModel === 'sets' && !plan.setCount) {
    errors.push('Sets teaching model requires set count');
  }

  if (plan.constraints.isElective && !plan.constraints.optionBlock && plan.teachingModel === 'options') {
    errors.push('Option subjects should specify an option block');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};
